import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Calendar, Clock, Tag, PenSquare, FileText, Loader2, AlertCircle } from "lucide-react";
import { Link } from "react-router-dom";
import axios from "axios";

const FILTERS = [
  { id: "all", label: "All Manuscripts" },
  { id: "development", label: "Development" },
  { id: "frontend", label: "Frontend" },
  { id: "backend", label: "Backend" },
];

const Blog = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeFilter, setActiveFilter] = useState("all");
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  
  const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:5000";

  useEffect(() => {
    setIsLoggedIn(!!localStorage.getItem("token"));
    
    const fetchPosts = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${apiUrl}/api/blog`);
        setPosts(response.data);
      } catch (err) {
        setError("Failed to retrieve the latest articles. The server might be unreachable.");
      } finally {
        setLoading(false);
      }
    };
    fetchPosts();
  }, [apiUrl]);

  const filteredPosts = activeFilter === "all" ? posts : posts.filter((post) => post.category === activeFilter);
  
  const formatDate = (dateString) => new Intl.DateTimeFormat('en-US', { dateStyle: 'medium' }).format(new Date(dateString));

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center bg-[#FAFAFA] dark:bg-[#09090B]">
        <Loader2 className="w-8 h-8 animate-spin text-zinc-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center bg-[#FAFAFA] dark:bg-[#09090B] px-4">
        <div className="text-center p-8 rounded-3xl border border-zinc-200 dark:border-white/10 bg-white dark:bg-zinc-900 shadow-xl max-w-md w-full">
          <AlertCircle className="w-10 h-10 text-red-500 mx-auto mb-4" />
          <p className="text-base font-medium text-zinc-900 dark:text-zinc-100 mb-6">{error}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="px-6 py-2.5 bg-zinc-900 text-white dark:bg-zinc-100 dark:text-zinc-900 font-medium rounded-full hover:bg-zinc-800 dark:hover:bg-white transition-colors"
          >
            Attempt Recovery
          </button>
        </div>
      </div>
    );
  }

  return (
    <section id="blog" className="py-24 bg-[#FAFAFA] dark:bg-[#09090B] px-4 sm:px-6 relative overflow-hidden">
      
      <div className="max-w-7xl mx-auto relative z-10">
        <motion.div 
          initial={{ opacity: 0, y: 20 }} 
          whileInView={{ opacity: 1, y: 0 }} 
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          viewport={{ once: true, margin: "-100px" }}
          className="text-center mb-16 md:mb-20"
        >
          <div className="inline-flex items-center justify-center p-3 rounded-2xl bg-zinc-100 dark:bg-white/5 border border-zinc-200 dark:border-white/10 mb-6">
            <FileText className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
          </div>
          <h2 className="text-4xl md:text-5xl font-display font-semibold tracking-tight text-zinc-900 dark:text-white mb-6">
            Engineering <span className="text-zinc-400 dark:text-zinc-500">Journal</span>
          </h2>
          <p className="text-lg max-w-2xl mx-auto text-zinc-600 dark:text-zinc-400 leading-relaxed">
            Technical deep dives, architectural decisions, and thoughts on the evolving landscape of full-stack development.
          </p>

          {/* Admin Action */}
          {isLoggedIn && (
            <div className="mt-8 flex justify-center">
              <Link 
                to="/blog/new" 
                className="inline-flex items-center px-6 py-2.5 rounded-full bg-emerald-500 text-white font-medium hover:bg-emerald-600 transition-colors shadow-lg shadow-emerald-500/20"
              >
                <PenSquare className="w-4 h-4 mr-2" />
                Draft Manuscript
              </Link>
            </div>
          )}

          {/* Premium Segmented Control Filtering (Matches Projects.js & Skills.js) */}
          <div className="flex flex-wrap justify-center gap-2 mt-12 p-1.5 bg-zinc-100 dark:bg-zinc-900 border border-zinc-200 dark:border-white/10 rounded-full max-w-fit mx-auto">
            {FILTERS.map((filter) => (
              <button
                key={filter.id}
                onClick={() => setActiveFilter(filter.id)}
                className={`relative px-6 py-2.5 rounded-full text-sm font-medium transition-colors duration-200 ${
                  activeFilter === filter.id
                    ? "text-white dark:text-zinc-900"
                    : "text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-200"
                }`}
              >
                {activeFilter === filter.id && (
                  <motion.div
                    layoutId="active-blog-pill"
                    className="absolute inset-0 bg-zinc-900 dark:bg-zinc-100 rounded-full"
                    transition={{ type: "spring", stiffness: 400, damping: 30 }}
                  />
                )}
                <span className="relative z-10">{filter.label}</span>
              </button>
            ))}
          </div>
        </motion.div>

        {/* Fluid Grid with Framer Motion Layout */}
        <motion.div 
          layout 
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
        >
          <AnimatePresence mode="popLayout">
            {filteredPosts.map((post) => {
              const heroImage = post.image?.startsWith("http") ? post.image : `${apiUrl}/${post.image}`;
              
              return (
                <motion.article
                  layout
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                  key={post._id || post.slug}
                  className="group flex flex-col bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-white/10 rounded-3xl overflow-hidden hover:shadow-2xl hover:shadow-zinc-900/10 dark:hover:shadow-white/5 transition-all duration-500"
                >
                  {/* Image Container */}
                  <Link to={`/blog/${post.slug}`} className="relative aspect-[16/10] overflow-hidden bg-zinc-100 dark:bg-zinc-800 border-b border-zinc-200 dark:border-white/10 block">
                    <img 
                      src={heroImage} 
                      alt={post.title} 
                      className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-700 ease-out" 
                      loading="lazy"
                    />
                  </Link>
                  
                  <div className="p-8 flex flex-col flex-grow">
                    <div className="flex items-center gap-4 mb-4 text-[13px] font-medium text-zinc-500 dark:text-zinc-400">
                      <span className="flex items-center"><Calendar className="w-3.5 h-3.5 mr-1.5" />{formatDate(post.createdAt || Date.now())}</span>
                      <span className="flex items-center"><Clock className="w-3.5 h-3.5 mr-1.5" />{post.readTime || "5 min read"}</span>
                    </div>
                    
                    <Link to={`/blog/${post.slug}`}>
                      <h3 className="text-xl font-semibold text-zinc-900 dark:text-zinc-100 mb-3 tracking-tight group-hover:text-emerald-600 dark:group-hover:text-emerald-400 transition-colors">
                        {post.title}
                      </h3>
                    </Link>
                    
                    <p className="text-sm text-zinc-600 dark:text-zinc-400 mb-6 line-clamp-3 leading-relaxed flex-grow">
                      {post.excerpt}
                    </p>
                    
                    <div className="flex flex-wrap gap-2 mb-6">
                      {post.tags?.slice(0, 3).map((tag) => (
                        <span 
                          key={tag} 
                          className="inline-flex items-center px-2.5 py-1 rounded-md text-[10px] font-semibold tracking-wider uppercase bg-zinc-100 text-zinc-600 dark:bg-white/5 dark:text-zinc-400 border border-zinc-200 dark:border-white/5"
                        >
                          <Tag className="w-3 h-3 mr-1" />{tag}
                        </span>
                      ))}
                    </div>
                    
                    <div className="pt-5 border-t border-zinc-100 dark:border-white/10 mt-auto">
                      <Link 
                        to={`/blog/${post.slug}`} 
                        className="inline-flex items-center text-sm font-medium text-zinc-900 dark:text-zinc-100 hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors"
                      >
                        Read Manuscript
                        <motion.span
                          className="inline-block ml-1"
                          initial={{ x: 0 }}
                          whileHover={{ x: 4 }}
                        >
                          →
                        </motion.span>
                      </Link>
                    </div>
                  </div>
                </motion.article>
              );
            })}
          </AnimatePresence>
        </motion.div>

        {filteredPosts.length === 0 && !loading && !error && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-20 px-4"
          >
            <div className="w-16 h-16 bg-zinc-100 dark:bg-zinc-800 rounded-full flex items-center justify-center mx-auto mb-4">
              <FileText className="w-8 h-8 text-zinc-400" />
            </div>
            <h3 className="text-lg font-medium text-zinc-900 dark:text-zinc-100 mb-2">No manuscripts found</h3>
            <p className="text-zinc-500 dark:text-zinc-400">There are currently no articles published in this category.</p>
          </motion.div>
        )}
        
      </div>
    </section>
  );
};

export default Blog;